import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Link2, Plus, Trash2, ExternalLink, Loader2, Check, X, Globe, Facebook, Instagram, Linkedin } from 'lucide-react';
import Portal from './Portal';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PLATFORMS = [
  { id: 'facebook', name: 'Facebook', desc: 'Post to Facebook Pages', icon: 'fb', color: '#1877F2' },
  { id: 'instagram', name: 'Instagram', desc: 'Post to Instagram Business', icon: 'ig', color: '#E4405F' },
  { id: 'linkedin', name: 'LinkedIn', desc: 'Share to LinkedIn profile', icon: 'li', color: '#0A66C2' },
  { id: 'wordpress', name: 'WordPress', desc: 'Publish to WordPress sites', icon: 'wp', color: '#21759B' },
  { id: 'medium', name: 'Medium', desc: 'Publish to Medium', icon: 'md', color: '#000' },
  { id: 'webflow', name: 'Webflow', desc: 'Publish to Webflow CMS', icon: 'wf', color: '#4353FF' },
  { id: 'ghost', name: 'Ghost', desc: 'Publish to Ghost blogs', icon: 'gh', color: '#15171A' },
  { id: 'framer', name: 'Framer', desc: 'Coming soon', icon: 'fr', color: '#0055FF' },
];

const AVAILABLE_PLATFORMS = ['facebook', 'instagram', 'linkedin', 'wordpress', 'medium', 'webflow', 'ghost'];

const PLATFORM_FIELDS = {
  wordpress: [
    { key: 'site_name', label: 'Site Name', type: 'text', placeholder: 'My WordPress Blog', required: true, isTop: true },
    { key: 'site_url', label: 'Site URL', type: 'url', placeholder: 'https://mysite.com', required: true },
    { key: 'username', label: 'Username', type: 'text', placeholder: 'admin', required: true },
    { key: 'app_password', label: 'Application Password', type: 'password', placeholder: 'xxxx xxxx xxxx xxxx', required: true, help: 'WordPress → Users → Profile → Application Passwords' },
  ],
  facebook: [
    { key: 'site_name', label: 'Page Name', type: 'text', placeholder: 'My Business Page', required: true, isTop: true },
    { key: 'page_id', label: 'Page ID', type: 'text', placeholder: '123456789012345', required: true, help: 'Find in your Page Settings → About → Page ID' },
    { key: 'page_access_token', label: 'Page Access Token', type: 'password', placeholder: 'EAAxxxxxxx...', required: true, help: 'Generate via Meta Business Suite → Settings → Integrations → Access Tokens' },
  ],
  instagram: [
    { key: 'site_name', label: 'Account Name', type: 'text', placeholder: '@mybusiness', required: true, isTop: true },
    { key: 'instagram_business_account_id', label: 'Instagram Business Account ID', type: 'text', placeholder: '17841400000000', required: true, help: 'Found in Meta Business Suite → Instagram account settings' },
    { key: 'access_token', label: 'Access Token', type: 'password', placeholder: 'EAAxxxxxxx...', required: true, help: 'Same token used for Facebook, from Meta Business Suite' },
    { key: 'default_image_url', label: 'Default Post Image URL', type: 'url', placeholder: 'https://example.com/brand-image.jpg', required: true, help: 'Instagram requires an image for every post. Provide a default brand image URL.' },
  ],
  linkedin: [
    { key: 'site_name', label: 'Profile Name', type: 'text', placeholder: 'John Doe', required: true, isTop: true },
    { key: 'access_token', label: 'Access Token', type: 'password', placeholder: 'AQXxxxxxxx...', required: true, help: 'Generate via LinkedIn Developer Portal → My Apps → Auth' },
  ],
  medium: [
    { key: 'site_name', label: 'Profile Name', type: 'text', placeholder: 'My Medium', required: true, isTop: true },
    { key: 'token', label: 'Integration Token', type: 'password', placeholder: 'xxxxxxxx', required: true, help: 'Medium → Settings → Security → Integration Tokens' },
  ],
  webflow: [
    { key: 'site_name', label: 'Site Name', type: 'text', placeholder: 'My Webflow Site', required: true, isTop: true },
    { key: 'api_token', label: 'API Token', type: 'password', placeholder: 'xxxxxxxx', required: true, help: 'Webflow → Site Settings → Integrations → API Access' },
    { key: 'site_id', label: 'Site ID', type: 'text', placeholder: 'xxxxxxxxxxxxxxxx', required: true, help: 'Found in your Webflow Site Settings → General' },
    { key: 'collection_id', label: 'Blog Collection ID', type: 'text', placeholder: 'xxxxxxxxxxxxxxxx', required: true, help: 'The CMS Collection ID for your blog posts' },
  ],
  ghost: [
    { key: 'site_name', label: 'Site Name', type: 'text', placeholder: 'My Ghost Blog', required: true, isTop: true },
    { key: 'api_url', label: 'Ghost API URL', type: 'url', placeholder: 'https://myblog.ghost.io', required: true, help: 'Your Ghost site URL (e.g., https://myblog.ghost.io)' },
    { key: 'admin_api_key', label: 'Admin API Key', type: 'password', placeholder: 'id:secret', required: true, help: 'Ghost Admin → Settings → Integrations → Custom → Admin API Key (format: id:secret)' },
  ],
};

const PlatformIcon = ({ platformId, size = 20 }) => {
  const iconMap = {
    facebook: <Facebook size={size} />,
    instagram: <Instagram size={size} />,
    linkedin: <Linkedin size={size} />,
  };
  return iconMap[platformId] || <Globe size={size} />;
};

const Integrations = () => {
  const { getAuthHeaders, subscription } = useAuth();
  const [integrations, setIntegrations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [addPlatform, setAddPlatform] = useState('');
  const [formData, setFormData] = useState({});
  const [adding, setAdding] = useState(false);
  const [testing, setTesting] = useState(null);
  const [testResult, setTestResult] = useState(null);

  useEffect(() => { fetchIntegrations(); }, []);

  const fetchIntegrations = async () => {
    try {
      const response = await axios.get(`${API}/integrations`, { headers: getAuthHeaders() });
      setIntegrations(response.data);
    } catch (err) { console.error('Failed to fetch integrations:', err); }
    finally { setLoading(false); }
  };

  const openAddModal = (platformId) => {
    setAddPlatform(platformId);
    setFormData({});
    setTestResult(null);
    setShowAddModal(true);
  };

  const handleAdd = async (e) => {
    e.preventDefault();
    setAdding(true);
    try {
      const fields = PLATFORM_FIELDS[addPlatform] || [];
      const siteName = formData.site_name || addPlatform;
      const credentials = {};
      fields.forEach(f => {
        if (!f.isTop) credentials[f.key] = formData[f.key] || '';
      });
      // For WordPress, also include site_url in top-level
      const siteUrl = formData.site_url || '';

      const payload = {
        platform: addPlatform,
        site_name: siteName,
        credentials,
      };

      await axios.post(`${API}/integrations`, payload, { headers: getAuthHeaders() });
      await fetchIntegrations();
      setShowAddModal(false);
      setFormData({});
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to add integration');
    } finally { setAdding(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Remove this integration?')) return;
    try {
      await axios.delete(`${API}/integrations/${id}`, { headers: getAuthHeaders() });
      setIntegrations(integrations.filter(i => i.id !== id));
    } catch (err) { console.error('Failed to delete:', err); }
  };

  const handleTestConnection = async (platform, creds) => {
    setTesting(platform);
    setTestResult(null);
    try {
      const response = await axios.post(`${API}/integrations/test`, {
        platform,
        credentials: creds
      }, { headers: getAuthHeaders() });
      setTestResult({ success: response.data.success, message: response.data.message });
    } catch (err) {
      setTestResult({ success: false, message: err.response?.data?.detail || 'Connection test failed' });
    } finally { setTesting(null); }
  };

  const handleTestFromForm = () => {
    const fields = PLATFORM_FIELDS[addPlatform] || [];
    const credentials = {};
    fields.forEach(f => {
      if (!f.isTop) credentials[f.key] = formData[f.key] || '';
    });
    handleTestConnection(addPlatform, credentials);
  };

  const maxSites = subscription?.max_sites || 1;

  if (loading) {
    return <div className="flex items-center justify-center h-64"><div className="spinner"></div></div>;
  }

  return (
    <div className="fade-in" data-testid="integrations-page">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-8">
        <div>
          <h1 className="font-serif text-3xl mb-1">Integrations</h1>
          <p className="text-sm text-[var(--color-muted)]">
            Connect your publishing platforms. {integrations.length} connected.
          </p>
        </div>
      </div>

      {/* Platform Cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 mb-8">
        {PLATFORMS.map((platform) => {
          const connected = integrations.filter(i => i.platform === platform.id);
          const isAvailable = AVAILABLE_PLATFORMS.includes(platform.id);
          return (
            <div
              key={platform.id}
              className={`card p-5 cursor-pointer transition-all hover:shadow-md ${!isAvailable ? 'opacity-40 pointer-events-none' : ''}`}
              onClick={() => isAvailable && openAddModal(platform.id)}
              data-testid={`platform-card-${platform.id}`}
            >
              <div className="flex items-start justify-between mb-3">
                <div className="w-10 h-10 rounded-xl flex items-center justify-center"
                     style={{ backgroundColor: `${platform.color}15` }}>
                  <PlatformIcon platformId={platform.id} size={20} />
                </div>
                {connected.length > 0 && (
                  <span className="badge badge-published text-[9px]">{connected.length} linked</span>
                )}
              </div>
              <h3 className="font-display text-sm font-medium">{platform.name}</h3>
              <p className="text-[10px] text-[var(--color-muted)] mt-0.5">
                {isAvailable ? (connected.length > 0 ? `${connected.length} connected` : 'Click to connect') : platform.desc}
              </p>
              {isAvailable && connected.length === 0 && (
                <div className="mt-2">
                  <span className="text-[10px] text-[var(--color-coral)] font-medium flex items-center gap-1">
                    <Plus className="w-3 h-3" /> Connect
                  </span>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Connected Integrations List */}
      {integrations.length > 0 && (
        <div>
          <h2 className="font-serif text-xl mb-4">Connected Accounts</h2>
          <div className="space-y-3">
            {integrations.map((integration) => {
              const platformInfo = PLATFORMS.find(p => p.id === integration.platform);
              return (
                <div key={integration.id} className="card p-5" data-testid={`integration-${integration.id}`}>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className="w-10 h-10 rounded-xl flex items-center justify-center"
                           style={{ backgroundColor: `${platformInfo?.color || '#666'}15` }}>
                        <PlatformIcon platformId={integration.platform} size={18} />
                      </div>
                      <div>
                        <h3 className="font-display text-sm font-medium">{integration.site_name}</h3>
                        <div className="flex items-center gap-2 text-[10px] text-[var(--color-muted)] mt-0.5">
                          <span className="capitalize">{integration.platform}</span>
                          {integration.site_url && (
                            <>
                              <span>&middot;</span>
                              <a href={integration.site_url} target="_blank" rel="noopener noreferrer"
                                className="flex items-center gap-0.5 hover:text-[var(--color-charcoal)]">
                                {integration.site_url} <ExternalLink className="w-2.5 h-2.5" />
                              </a>
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <button onClick={() => handleDelete(integration.id)} className="btn btn-ghost p-2" data-testid={`delete-integration-${integration.id}`}>
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {integrations.length === 0 && (
        <div className="card p-14 text-center">
          <div className="w-14 h-14 mx-auto rounded-full flex items-center justify-center mb-4 bg-[var(--color-surface)]">
            <Link2 className="w-7 h-7 text-[var(--color-coral)]" />
          </div>
          <h3 className="font-display text-base font-medium mb-2">No integrations yet</h3>
          <p className="text-sm text-[var(--color-muted)] mb-6">
            Connect Facebook, Instagram, LinkedIn, or WordPress to publish articles directly.
          </p>
          <button onClick={() => openAddModal('facebook')} className="btn btn-primary text-sm" data-testid="add-first-integration-btn">
            <Plus className="w-4 h-4" /> Connect a Platform
          </button>
        </div>
      )}

      {/* Add Integration Modal */}
      {showAddModal && addPlatform && (
        <Portal>
        <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-[20px] w-full max-w-md max-h-[90vh] overflow-y-auto shadow-2xl">
            <div className="p-6 border-b border-[var(--color-border)] flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-xl flex items-center justify-center"
                     style={{ backgroundColor: `${PLATFORMS.find(p => p.id === addPlatform)?.color || '#666'}15` }}>
                  <PlatformIcon platformId={addPlatform} size={18} />
                </div>
                <div>
                  <h2 className="font-serif text-xl">Connect {PLATFORMS.find(p => p.id === addPlatform)?.name}</h2>
                  <p className="text-[10px] text-[var(--color-muted)]">Enter your credentials below</p>
                </div>
              </div>
              <button onClick={() => setShowAddModal(false)} className="btn btn-ghost p-2">
                <X className="w-5 h-5" />
              </button>
            </div>
            <form onSubmit={handleAdd} className="p-6 space-y-4">
              {(PLATFORM_FIELDS[addPlatform] || []).map((field) => (
                <div key={field.key}>
                  <label className="block text-xs font-medium mb-1.5">{field.label} {field.required && '*'}</label>
                  <input
                    type={field.type}
                    value={formData[field.key] || ''}
                    onChange={(e) => setFormData({...formData, [field.key]: e.target.value})}
                    className="input"
                    placeholder={field.placeholder}
                    required={field.required}
                    data-testid={`field-${field.key}`}
                  />
                  {field.help && (
                    <p className="text-[10px] text-[var(--color-muted)] mt-1">{field.help}</p>
                  )}
                </div>
              ))}

              {/* Test Result */}
              {testResult && (
                <div className={`flex items-start gap-2 p-3 rounded-xl text-xs ${
                  testResult.success ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'
                }`} data-testid="test-result">
                  {testResult.success ? <Check className="w-4 h-4 mt-0.5 flex-shrink-0" /> : <X className="w-4 h-4 mt-0.5 flex-shrink-0" />}
                  <span>{testResult.message}</span>
                </div>
              )}

              <div className="pt-4 space-y-3">
                <button
                  type="button"
                  onClick={handleTestFromForm}
                  disabled={testing}
                  className="btn btn-secondary w-full"
                  data-testid="test-connection-btn"
                >
                  {testing ? <><Loader2 className="w-4 h-4 animate-spin" /> Testing...</> : 'Test Connection'}
                </button>
                <div className="flex gap-3">
                  <button type="button" onClick={() => setShowAddModal(false)} className="btn btn-secondary flex-1">Cancel</button>
                  <button type="submit" disabled={adding} className="btn btn-primary flex-1" data-testid="save-integration-btn">
                    {adding ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Connect'}
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
        </Portal>
      )}
    </div>
  );
};

export default Integrations;
